import { decamelizeKeys } from 'humps';
import resolve from '../../../../api-client';

import {
  SURVEYS__CREATE,
  SURVEYS__COPY,
  SURVEYS__DELETE,
  SURVEYS__GET,
  SURVEYS__GET_TAGGED,
  SURVEYS__FETCH,
  SURVEYS__FETCH_ACTIVE,
  SURVEYS__FETCH_FRAUD_DETECTION_SETTINGS,
  SURVEYS__FETCH_HASH,
  SURVEYS__FETCH_RECENT,
  SURVEYS__MOVE,
  SURVEYS__PATCH,
  SURVEYS_REVERSE_LOOKUP__FETCH,
  SURVEYS__RESET,
  SURVEYS__UPDATE,
  SURVEYS__UPDATE_ACTION,
  SURVEYS__UPDATE_ACTION__REVIEW,
  SURVEYS__UPDATE_ACTION__PUBLISH,
  SURVEYS__UPDATE_FRAUD_DETECTION_SETTINGS,
} from '../constants';
import { confirm } from '../confirm-modal/actions.confirm-modal';
import {
  getFraudDetectionSettingsUrl,
  getFraudDetectionSettingsItemUrl,
  getSurveysItemUrl,
  getSurveysActiveUrl,
  getSurveysByTagId,
  getSurveysCopyUrl,
  getSurveyHashUrl,
  getSurveysMoveUrl,
  getSurveysUrl,
  getSurveysRecentUrl,
  getReverseLookupUrl,
} from '../../util';

export const surveysCopy = (organizationId, surveyId) =>
  resolve({
    method: 'post',
    type: SURVEYS__COPY,
    url: getSurveysCopyUrl(surveyId),
    data: decamelizeKeys({
      organizationId,
    }),
  });
export const surveysCreate = (organizationId, survey) =>
  resolve({
    method: 'post',
    type: SURVEYS__CREATE,
    url: getSurveysUrl(),
    data: decamelizeKeys({
      ...survey,
      organizationId,
    }),
  });

export const surveysDelete = (organizationId, surveyId) =>
  resolve({
    method: 'delete',
    type: SURVEYS__DELETE,
    url: getSurveysItemUrl(surveyId),
    params: decamelizeKeys({
      organizationId,
      surveyId,
    }),
  });

export const surveysFetch = (organizationId) =>
  resolve({
    type: SURVEYS__FETCH,
    url: getSurveysUrl(),
    params: decamelizeKeys({ organizationId }),
  });
export const surveysFetchRecent = (organizationId) =>
  resolve({
    type: SURVEYS__FETCH_RECENT,
    url: getSurveysRecentUrl(),
    params: decamelizeKeys({ organizationId }),
  });
export const surveysFetchHash = (organizationId, surveyId, surveyHash) =>
  resolve({
    type: SURVEYS__FETCH_HASH,
    url: getSurveyHashUrl(surveyId),
    params: decamelizeKeys({ organizationId, surveyId }),
    surveyHash,
  });
export const surveysFetchFraudDetectionSettings = (organizationId, surveyId) =>
  resolve({
    type: SURVEYS__FETCH_FRAUD_DETECTION_SETTINGS,
    url: getFraudDetectionSettingsUrl(),
    params: decamelizeKeys({ organizationId, surveyId }),
    surveyId,
  });

export const surveysFetchActive = (surveyId, resultToken) =>
  resolve({
    type: SURVEYS__FETCH_ACTIVE,
    url: getSurveysActiveUrl(surveyId),
    params: decamelizeKeys(resultToken ? { surveyId, resultToken } : { surveyId }),
  });

export const surveysFetchItem = (organizationId, surveyId) =>
  resolve({
    type: SURVEYS__GET,
    url: getSurveysItemUrl(surveyId),
    params: decamelizeKeys({
      organizationId,
      surveyId,
    }),
  });

export const surveysFetchTagged = (organizationId, tagId) =>
  resolve({
    type: SURVEYS__GET_TAGGED,
    url: getSurveysByTagId(tagId),
    params: decamelizeKeys({
      organizationId,
      id: tagId,
    }),
  });

export const surveysMove = (id, organizationId, targetOrganizationId) =>
  resolve({
    method: 'post',
    type: SURVEYS__MOVE,
    url: getSurveysMoveUrl(id),
    data: decamelizeKeys({ id, organizationId, targetOrganizationId }),
  });

const patch = (surveyId, newProps) => ({
  type: SURVEYS__PATCH,
  data: {
    id: surveyId,
    ...newProps,
  },
});
const update = (surveyId, organizationId, newProps, type = SURVEYS__UPDATE) => ({
  method: 'put',
  type,
  url: getSurveysItemUrl(surveyId),
  data: decamelizeKeys({
    id: surveyId,
    organizationId,
    ...newProps,
  }),
});
export const surveysUpdate =
  (organizationId, survey, rawProps, patchOnly = false) =>
  async (dispatch) => {
    const surveyId = survey.id;
    const newProps = rawProps;
    const patchAction = patch(surveyId, newProps);
    dispatch(patchAction);

    if (patchOnly) {
      return Promise.resolve();
    }

    try {
      const payload = update(surveyId, organizationId, newProps);
      const updateAction = resolve(payload);
      return dispatch(updateAction);
    } catch (error) {
      const revertAction = patch(surveyId, survey);
      return dispatch(revertAction);
    }
  };

export const surveysUpdateFetchFraudDetectionSettings = (
  organizationId,
  surveyId,
  settingId,
  setting,
  prevSetting,
) =>
  resolve({
    method: 'patch',
    type: SURVEYS__UPDATE_FRAUD_DETECTION_SETTINGS,
    url: getFraudDetectionSettingsItemUrl(settingId),
    data: decamelizeKeys({
      organizationId,
      surveyId,
      id: settingId,
      setting,
    }),
    surveyId,
    prevSetting,
  });

export const surveysSetUpdateAction = (
  organizationId,
  surveyId,
  updateAction,
  ignoreWarnings = true,
) => {
  let action;
  switch (updateAction) {
    case 'publish':
      action = SURVEYS__UPDATE_ACTION__PUBLISH;
      break;
    case 'review':
      action = SURVEYS__UPDATE_ACTION__REVIEW;
      break;
    default:
      action = SURVEYS__UPDATE_ACTION;
  }
  return resolve(update(surveyId, organizationId, { ignoreWarnings, updateAction }, action));
};

const makeUpdateAction = (dispatch, organizationId, surveyId) => (updateAction) => () => {
  const action = surveysSetUpdateAction(organizationId, surveyId, updateAction);
  return dispatch(action);
};
export const surveyMakeUpdateActions = (dispatch, organizationId, surveyId) => {
  const confirmStop = () => dispatch(confirm('Stop Survey?'));
  const makeAction = makeUpdateAction(dispatch, organizationId, surveyId);
  return {
    pause: makeAction('pause'),
    publish: makeAction('review'),
    stop() {
      confirmStop().then(makeAction('stop'));
    },
    unpause: makeAction('unpause'),
  };
};

export const surveysReverseLookupFetch = (criteria) =>
  resolve({
    method: 'get',
    type: SURVEYS_REVERSE_LOOKUP__FETCH,
    url: getReverseLookupUrl(),
    params: {
      criteria,
    },
  });

export const surveysReset = {
  type: SURVEYS__RESET,
};
